import { resolveInputValue } from "@/modules/_shared/templateValue";
import type { FindReplaceOp } from "@/lib/mapper/types";
import type { CellModule } from "@/modules/_shared/contract";

/** Escape a literal string so it can be used inside a RegExp source. */
function escapeRegExp(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/**
 * Apply one find→replace op to `value`. Pure — never throws. An invalid regex
 * (when `regex` is true) is skipped, returning the value unchanged.
 */
export function applyFindReplaceOp(value: string, op: FindReplaceOp): string {
  if (!op || typeof op.find !== "string" || op.find === "") return value;
  const replace = typeof op.replace === "string" ? op.replace : "";

  if (op.regex) {
    try {
      const re = new RegExp(op.find, op.caseInsensitive ? "gi" : "g");
      return value.replace(re, replace);
    } catch {
      return value; // invalid regex — skip this rule
    }
  }

  // Literal replace-all. Use a compiled regex over the escaped source so we can
  // honour case-insensitivity and replace every occurrence in one pass.
  try {
    const re = new RegExp(escapeRegExp(op.find), op.caseInsensitive ? "gi" : "g");
    // Escape `$` in the replacement so literal mode never interprets $1 etc.
    return value.replace(re, replace.replace(/\$/g, "$$$$"));
  } catch {
    return value;
  }
}

/** Run an ordered list of find→replace ops over a string. Pure / total. */
export function applyFindReplace(value: string, rules: FindReplaceOp[]): string {
  if (!Array.isArray(rules)) return value;
  let out = value;
  for (const op of rules) out = applyFindReplaceOp(out, op);
  return out;
}

export const findReplaceModule: CellModule = {
  kind: "find-replace",
  capability: "cell",
  name: "Find & Replace",
  tooltip: "Apply a list of find→replace edits to text",
  description:
    "Takes an input value and applies your find→replace edits in order. Each edit can be a plain text swap or a regular expression, optionally case-insensitive.",
  category: "Rules",
  tags: ["replace", "find", "regex", "substitute", "sed", "clean", "text"],
  icon: { kind: "lucide", name: "Replace" },
  tone: "sky",
  defaultConfig: () => ({
    kind: "find-replace",
    input: "",
    rules: [{ find: "", replace: "" }],
  }),
  resolve: (rule, { row, helpers, output }) => {
    if (rule.kind !== "find-replace") return "";
    const input = resolveInputValue(rule.input, { row, helpers, output });
    return applyFindReplace(input, rule.rules ?? []);
  },
  templateStrings: (rule) =>
    rule.kind === "find-replace" ? [rule.input] : [],
};
